/**
 * Country Codes
 *
 * Common country dial codes for phone number input.
 * Includes timezone-based auto-detection for default country.
 */

// digits: expected local phone number length (without country code)
// Can be a single number or [min, max] for countries with variable lengths
export const COUNTRY_CODES = [
  { code: 'IN', dialCode: '+91', name: 'India', digits: 10 },
  { code: 'US', dialCode: '+1', name: 'United States', digits: 10 },
  { code: 'GB', dialCode: '+44', name: 'United Kingdom', digits: 10 },
  { code: 'CA', dialCode: '+1', name: 'Canada', digits: 10 },
  { code: 'AU', dialCode: '+61', name: 'Australia', digits: 9 },
  { code: 'DE', dialCode: '+49', name: 'Germany', digits: [10, 11] },
  { code: 'FR', dialCode: '+33', name: 'France', digits: 9 },
  { code: 'IT', dialCode: '+39', name: 'Italy', digits: 10 },
  { code: 'ES', dialCode: '+34', name: 'Spain', digits: 9 },
  { code: 'PT', dialCode: '+351', name: 'Portugal', digits: 9 },
  { code: 'NL', dialCode: '+31', name: 'Netherlands', digits: 9 },
  { code: 'BE', dialCode: '+32', name: 'Belgium', digits: [8, 9] },
  { code: 'CH', dialCode: '+41', name: 'Switzerland', digits: 9 },
  { code: 'AT', dialCode: '+43', name: 'Austria', digits: [10, 11] },
  { code: 'SE', dialCode: '+46', name: 'Sweden', digits: [7, 10] },
  { code: 'NO', dialCode: '+47', name: 'Norway', digits: 8 },
  { code: 'DK', dialCode: '+45', name: 'Denmark', digits: 8 },
  { code: 'FI', dialCode: '+358', name: 'Finland', digits: [8, 10] },
  { code: 'IE', dialCode: '+353', name: 'Ireland', digits: [7, 9] },
  { code: 'PL', dialCode: '+48', name: 'Poland', digits: 9 },
  { code: 'RU', dialCode: '+7', name: 'Russia', digits: 10 },
  { code: 'CN', dialCode: '+86', name: 'China', digits: 11 },
  { code: 'JP', dialCode: '+81', name: 'Japan', digits: [10, 11] },
  { code: 'KR', dialCode: '+82', name: 'South Korea', digits: [10, 11] },
  { code: 'SG', dialCode: '+65', name: 'Singapore', digits: 8 },
  { code: 'MY', dialCode: '+60', name: 'Malaysia', digits: [9, 10] },
  { code: 'ID', dialCode: '+62', name: 'Indonesia', digits: [10, 12] },
  { code: 'TH', dialCode: '+66', name: 'Thailand', digits: 9 },
  { code: 'PH', dialCode: '+63', name: 'Philippines', digits: 10 },
  { code: 'VN', dialCode: '+84', name: 'Vietnam', digits: [9, 10] },
  { code: 'BD', dialCode: '+880', name: 'Bangladesh', digits: 10 },
  { code: 'PK', dialCode: '+92', name: 'Pakistan', digits: 10 },
  { code: 'LK', dialCode: '+94', name: 'Sri Lanka', digits: 9 },
  { code: 'NP', dialCode: '+977', name: 'Nepal', digits: 10 },
  { code: 'AE', dialCode: '+971', name: 'UAE', digits: 9 },
  { code: 'SA', dialCode: '+966', name: 'Saudi Arabia', digits: 9 },
  { code: 'QA', dialCode: '+974', name: 'Qatar', digits: 8 },
  { code: 'KW', dialCode: '+965', name: 'Kuwait', digits: 8 },
  { code: 'OM', dialCode: '+968', name: 'Oman', digits: 8 },
  { code: 'BH', dialCode: '+973', name: 'Bahrain', digits: 8 },
  { code: 'IL', dialCode: '+972', name: 'Israel', digits: [9, 10] },
  { code: 'TR', dialCode: '+90', name: 'Turkey', digits: 10 },
  { code: 'EG', dialCode: '+20', name: 'Egypt', digits: 10 },
  { code: 'ZA', dialCode: '+27', name: 'South Africa', digits: 9 },
  { code: 'NG', dialCode: '+234', name: 'Nigeria', digits: 10 },
  { code: 'KE', dialCode: '+254', name: 'Kenya', digits: 9 },
  { code: 'GH', dialCode: '+233', name: 'Ghana', digits: 9 },
  { code: 'BR', dialCode: '+55', name: 'Brazil', digits: 11 },
  { code: 'MX', dialCode: '+52', name: 'Mexico', digits: 10 },
  { code: 'AR', dialCode: '+54', name: 'Argentina', digits: 10 },
  { code: 'CO', dialCode: '+57', name: 'Colombia', digits: 10 },
  { code: 'CL', dialCode: '+56', name: 'Chile', digits: 9 },
  { code: 'PE', dialCode: '+51', name: 'Peru', digits: 9 },
  { code: 'NZ', dialCode: '+64', name: 'New Zealand', digits: [8, 10] },
]

/**
 * Map of common timezones to country codes.
 * Used for auto-detecting the user's country from their browser timezone.
 */
const TIMEZONE_TO_COUNTRY = {
  // India
  'Asia/Kolkata': 'IN',
  'Asia/Calcutta': 'IN',
  // United States
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Los_Angeles': 'US',
  'America/Phoenix': 'US',
  'America/Anchorage': 'US',
  'Pacific/Honolulu': 'US',
  'America/Detroit': 'US',
  'America/Indiana/Indianapolis': 'US',
  // Canada
  'America/Toronto': 'CA',
  'America/Vancouver': 'CA',
  'America/Edmonton': 'CA',
  'America/Winnipeg': 'CA',
  'America/Halifax': 'CA',
  // United Kingdom
  'Europe/London': 'GB',
  // Germany
  'Europe/Berlin': 'DE',
  // France
  'Europe/Paris': 'FR',
  // Italy
  'Europe/Rome': 'IT',
  // Spain
  'Europe/Madrid': 'ES',
  // Portugal
  'Europe/Lisbon': 'PT',
  // Netherlands
  'Europe/Amsterdam': 'NL',
  // Belgium
  'Europe/Brussels': 'BE',
  // Switzerland
  'Europe/Zurich': 'CH',
  // Austria
  'Europe/Vienna': 'AT',
  // Sweden
  'Europe/Stockholm': 'SE',
  // Norway
  'Europe/Oslo': 'NO',
  // Denmark
  'Europe/Copenhagen': 'DK',
  // Finland
  'Europe/Helsinki': 'FI',
  // Ireland
  'Europe/Dublin': 'IE',
  // Poland
  'Europe/Warsaw': 'PL',
  // Russia
  'Europe/Moscow': 'RU',
  // China
  'Asia/Shanghai': 'CN',
  'Asia/Hong_Kong': 'CN',
  // Japan
  'Asia/Tokyo': 'JP',
  // South Korea
  'Asia/Seoul': 'KR',
  // Singapore
  'Asia/Singapore': 'SG',
  // Malaysia
  'Asia/Kuala_Lumpur': 'MY',
  // Indonesia
  'Asia/Jakarta': 'ID',
  // Thailand
  'Asia/Bangkok': 'TH',
  // Philippines
  'Asia/Manila': 'PH',
  // Vietnam
  'Asia/Ho_Chi_Minh': 'VN',
  // Bangladesh
  'Asia/Dhaka': 'BD',
  // Pakistan
  'Asia/Karachi': 'PK',
  // Sri Lanka
  'Asia/Colombo': 'LK',
  // Nepal
  'Asia/Kathmandu': 'NP',
  // UAE
  'Asia/Dubai': 'AE',
  // Saudi Arabia
  'Asia/Riyadh': 'SA',
  // Qatar
  'Asia/Qatar': 'QA',
  // Kuwait
  'Asia/Kuwait': 'KW',
  // Israel
  'Asia/Jerusalem': 'IL',
  // Turkey
  'Europe/Istanbul': 'TR',
  // Egypt
  'Africa/Cairo': 'EG',
  // South Africa
  'Africa/Johannesburg': 'ZA',
  // Nigeria
  'Africa/Lagos': 'NG',
  // Kenya
  'Africa/Nairobi': 'KE',
  // Brazil
  'America/Sao_Paulo': 'BR',
  // Mexico
  'America/Mexico_City': 'MX',
  // Argentina
  'America/Argentina/Buenos_Aires': 'AR',
  // Colombia
  'America/Bogota': 'CO',
  // Chile
  'America/Santiago': 'CL',
  // Peru
  'America/Lima': 'PE',
  // Australia
  'Australia/Sydney': 'AU',
  'Australia/Melbourne': 'AU',
  'Australia/Brisbane': 'AU',
  'Australia/Perth': 'AU',
  'Australia/Adelaide': 'AU',
  // New Zealand
  'Pacific/Auckland': 'NZ',
}

/**
 * Detect the user's country code based on their browser timezone.
 * Falls back to "IN" (India) if timezone cannot be determined.
 *
 * @returns {string} ISO country code (e.g., "IN", "US")
 */
export function detectCountryFromTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    return TIMEZONE_TO_COUNTRY[tz] || 'IN'
  } catch {
    return 'IN'
  }
}

/**
 * Get the default dial code based on user's timezone.
 *
 * @returns {string} Dial code with + prefix (e.g., "+91")
 */
export function getDefaultDialCode() {
  const countryCode = detectCountryFromTimezone()
  const country = COUNTRY_CODES.find((c) => c.code === countryCode)
  return country?.dialCode || '+91'
}

/**
 * Validate a phone number based on the selected country code.
 *
 * @param {string} dialCode - Country dial code (e.g., "+91")
 * @param {string} number - Phone number (digits only, stripped of spaces/dashes)
 * @returns {{ valid: boolean, message?: string }} Validation result
 */
export function validatePhoneNumber(dialCode, number) {
  if (!number || number.trim() === '') {
    return { valid: false, message: 'Phone number is required' }
  }

  const digits = number.replace(/[\s-]/g, '')

  if (!/^\d+$/.test(digits)) {
    return { valid: false, message: 'Phone number should contain only digits' }
  }

  // Find the country for this dial code
  const country = COUNTRY_CODES.find((c) => c.dialCode === dialCode)

  if (country && country.digits) {
    const expectedDigits = country.digits
    if (Array.isArray(expectedDigits)) {
      // Variable length: [min, max]
      const [min, max] = expectedDigits
      if (digits.length < min || digits.length > max) {
        return {
          valid: false,
          message: `${country.name} phone numbers should be ${min}-${max} digits (entered ${digits.length})`,
        }
      }
    } else {
      // Fixed length
      if (digits.length !== expectedDigits) {
        return {
          valid: false,
          message: `${country.name} phone numbers should be ${expectedDigits} digits (entered ${digits.length})`,
        }
      }
    }
  } else {
    // Fallback: generic validation for unknown countries
    if (digits.length < 7 || digits.length > 15) {
      return { valid: false, message: 'Phone number should be 7-15 digits' }
    }
  }

  return { valid: true }
}
